import React from "react";
import { cn } from "../../../utils/cn";
import type { AgendaViewport } from "../agendaBreakpoints";

const BLOCK = "animate-pulse rounded-md bg-[#E2E8F0]";

const GRID_HOURS = 10;
const GRID_ROWS = 6;
const MOBILE_ROWS = 6;

function Bar({ className }: { className?: string }) {
  return <span className={cn("block", BLOCK, className)} aria-hidden />;
}

/** Same shell as KpiCards (4 cards on desktop). */
export function AgendaKpiSkeleton() {
  return (
    <div className="mb-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4" aria-hidden>
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className="flex min-h-[96px] flex-col gap-3 rounded-xl border border-[#E8ECF1] bg-[#F7FAFC] px-4 py-3.5"
        >
          <Bar className="h-3.5 w-28" />
          <Bar className="h-7 w-16" />
          <Bar className="h-3 w-40" />
        </div>
      ))}
    </div>
  );
}

function AgendaToolbarSkeleton() {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" aria-hidden>
      <Bar className="h-10 w-48" />
      <Bar className="h-10 w-40" />
      <Bar className="h-10 w-40" />
      <Bar className="ml-auto h-10 w-56 max-sm:ml-0" />
    </div>
  );
}

/** Consultant rows x hour columns, matching AgendaScheduleCard. */
export function AgendaGridSkeleton({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn("overflow-hidden rounded-xl border border-[#E2E8F0] bg-[#F7FAFC]", className)}
    >
      <span className="sr-only">Carregando agenda...</span>
      <div className="overflow-x-auto px-3 pb-3 pt-3.5">
        <div className="min-w-[900px]">
          <div className="mb-2 grid grid-cols-[180px_repeat(10,minmax(0,1fr))] gap-2">
            <Bar className="h-4 w-24" />
            {Array.from({ length: GRID_HOURS }, (_, index) => (
              <Bar key={index} className="mx-auto h-4 w-8" />
            ))}
          </div>

          {Array.from({ length: GRID_ROWS }, (_, row) => (
            <div
              key={row}
              className="mb-2 grid grid-cols-[180px_repeat(10,minmax(0,1fr))] gap-2"
            >
              <div className="flex items-center gap-2 rounded-lg bg-white px-2 py-2">
                <Bar className="h-8 w-8 shrink-0 rounded-full" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Bar className="h-3 w-20" />
                  <Bar className="h-2.5 w-14" />
                </div>
              </div>
              {Array.from({ length: GRID_HOURS }, (_, column) => (
                <div
                  key={column}
                  className="h-[58px] animate-pulse rounded-lg border border-dashed border-[#E2E8F0] bg-white"
                  style={{ animationDelay: `${(row + column) * 40}ms` }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Timeline list used on mobile (MobileScheduleList). */
export function AgendaMobileListSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className="flex flex-col gap-2.5">
      <span className="sr-only">Carregando agenda...</span>
      <Bar className="mb-1 h-4 w-40" />
      {Array.from({ length: MOBILE_ROWS }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-3 py-3"
          aria-hidden
        >
          <Bar className="h-4 w-11 shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Bar className="h-3.5 w-3/5" />
            <Bar className="h-3 w-2/5" />
          </div>
          <Bar className="h-5 w-14 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function AgendaMobileWeekSkeleton() {
  return (
    <div className="mb-4 grid grid-cols-7 gap-1.5" aria-hidden>
      {Array.from({ length: 7 }, (_, index) => (
        <Bar key={index} className="h-14 rounded-xl" />
      ))}
    </div>
  );
}

/** First load: the whole page structure while nothing came from the API yet. */
export function AgendaPageSkeleton({ viewport }: { viewport: AgendaViewport }) {
  if (viewport === "mobile") {
    return (
      <div className="px-3">
        <div className="mb-4 flex items-center justify-between" aria-hidden>
          <Bar className="h-6 w-32" />
          <Bar className="h-9 w-9 rounded-full" />
        </div>
        <AgendaMobileWeekSkeleton />
        <AgendaMobileListSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3" aria-hidden>
        <Bar className="h-8 w-40" />
        <div className="flex gap-2">
          <Bar className="h-10 w-64" />
          <Bar className="h-10 w-36" />
        </div>
      </div>
      {viewport === "desktop" ? <AgendaKpiSkeleton /> : null}
      <AgendaToolbarSkeleton />
      <AgendaGridSkeleton />
    </div>
  );
}
